"use client"

import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Paperclip} from "lucide-react";
import {Button} from "@/components/ui/button";
import {FileUpload, FileUploadTrigger} from "@/app/components/FileUpload";
import {FilePreviewList} from "@/app/components/FilePreviewList";
import {LinkPreviewList} from "@/app/components/editor/link-preview/LinkPreviewList";
import {FileDropOverlay} from "@/app/components/FileDropOverlay";
import {AttachmentInfo, useUnifiedFileUpload} from "@/hooks/use-unified-file-upload";
import {useConfluence, useJira, useLinear, useNotion} from "@/hooks/queries/use-integrations";
import {externalLinkToIssueAttachmentMetadata, extractAllLinks, getExternalLinkKey, issueAttachmentMetadataForExternalLinks, previewToIssueAttachmentMetadata, type LinkProvider} from "@/lib/link-preview-utils";
import type {ExternalLink, ExternalLinkPreview, IssueAttachmentMetadata} from "@/pb/api/devplan/types/attachments";
import {gaId} from "@/lib/analytics/clickTracker";
import {cn} from "@/lib/utils";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";

export interface CreationAttachmentsPayload {
  imageUrls: string[];
  attachmentIds: string[];
  issueAttachments: IssueAttachmentMetadata[];
}

interface AttachmentsAndIssueLinksInputProps {
  companyId: number;
  description: string;
  disabled?: boolean;
  className?: string;
  hideUploadTrigger?: boolean;
  footer?: React.ReactNode | ((args: {
    uploadTrigger: React.ReactNode;
  }) => React.ReactNode);
  children?: React.ReactNode | ((args: {
    handlePaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
    uploadTrigger: React.ReactNode;
  }) => React.ReactNode);
  onChange?: (payload: CreationAttachmentsPayload) => void;
}

export function AttachmentsAndIssueLinksInput({
  companyId,
  description,
  disabled,
  className,
  hideUploadTrigger,
  footer,
  children,
  onChange,
}: AttachmentsAndIssueLinksInputProps) {
  const {
    imageUrls,
    setImageUrls,
    attachments,
    setAttachments,
    dragActive,
    dragHandlers,
    handlePaste,
    buttonTitle,
    acceptedFileTypes,
    fileUploadProps,
  } = useUnifiedFileUpload({
    companyId,
    disabled,
  })

  const {info: jiraInfo, loading: jiraLoading} = useJira(companyId);
  const {info: linearInfo, loading: linearLoading} = useLinear(companyId);
  const {info: notionInfo, loading: notionLoading} = useNotion(companyId);
  const {info: confluenceInfo, loading: confluenceLoading} = useConfluence(companyId);
  const hasJiraIntegration = !jiraLoading && jiraInfo !== undefined;
  const hasLinearIntegration = !linearLoading && linearInfo !== undefined;
  const hasNotionIntegration = !notionLoading && notionInfo !== undefined;
  const hasConfluenceIntegration = !confluenceLoading && confluenceInfo !== undefined;
  const activeLinkProviders = useMemo<LinkProvider[]>(() => {
    const providers: LinkProvider[] = ["google-drive"];
    if (hasJiraIntegration) providers.push("jira");
    if (hasLinearIntegration) providers.push("linear", "linear-project");
    if (hasNotionIntegration) providers.push("notion");
    if (hasConfluenceIntegration) providers.push("confluence");
    return providers;
  }, [hasConfluenceIntegration, hasJiraIntegration, hasLinearIntegration, hasNotionIntegration]);
  const [detectedLinks, setDetectedLinks] = useState<ExternalLink[]>([]);
  const [dismissedLinkKeys, setDismissedLinkKeys] = useState<Set<string>>(() => new Set());
  const [issueAttachmentsByKey, setIssueAttachmentsByKey] = useState<Record<string, IssueAttachmentMetadata>>({});

  useEffect(() => {
    if (activeLinkProviders.length === 0) {
      setDetectedLinks([]);
      return;
    }

    const links = extractAllLinks(description || "", activeLinkProviders);
    const activeKeys = new Set(links.map(getExternalLinkKey));
    setDismissedLinkKeys(prev => {
      const next = new Set([...prev].filter(key => activeKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
    setDetectedLinks(links.filter(link => !dismissedLinkKeys.has(getExternalLinkKey(link))));
  }, [activeLinkProviders, description, dismissedLinkKeys]);

  const handleRemoveLink = useCallback((link: ExternalLink) => {
    const key = getExternalLinkKey(link);
    setDismissedLinkKeys(prev => new Set(prev).add(key));
    setDetectedLinks(prev => prev.filter(item => getExternalLinkKey(item) !== key));
    setIssueAttachmentsByKey(prev => {
      const next = {...prev};
      delete next[key];
      return next;
    });
  }, []);

  useEffect(() => {
    setIssueAttachmentsByKey(prev => {
      const next: Record<string, IssueAttachmentMetadata> = {};
      for (const link of detectedLinks) {
        const key = getExternalLinkKey(link);
        const fallback = externalLinkToIssueAttachmentMetadata(link);
        if (prev[key] || fallback) {
          next[key] = prev[key] ?? fallback!;
        }
      }
      return next;
    });
  }, [detectedLinks]);

  const handlePreviewLoaded = useCallback((preview: ExternalLinkPreview, originalLink: ExternalLink) => {
    const issueAttachment = previewToIssueAttachmentMetadata(preview);
    const link = preview.link;
    if (!issueAttachment || !link?.provider || !link.identifier) {
      return;
    }
    setIssueAttachmentsByKey(prev => ({
      ...prev,
      [getExternalLinkKey(originalLink)]: issueAttachment,
    }));
  }, []);

  const payload = useMemo<CreationAttachmentsPayload>(() => {
    const latestLinks = extractAllLinks(description || "", activeLinkProviders)
      .filter(link => !dismissedLinkKeys.has(getExternalLinkKey(link)));
    return {
      imageUrls,
      attachmentIds: attachments.map(a => a.id),
      issueAttachments: issueAttachmentMetadataForExternalLinks(latestLinks, issueAttachmentsByKey),
    };
  }, [activeLinkProviders, attachments, description, dismissedLinkKeys, imageUrls, issueAttachmentsByKey]);

  useEffect(() => {
    onChange?.(payload)
  }, [onChange, payload]);

  const hasFiles = imageUrls.length > 0 || attachments.length > 0;
  const hasLinks = detectedLinks.length > 0;
  const uploadTrigger = (
    <FileUpload
      urlSigner={fileUploadProps.urlSigner}
      multiple
      allowedTypes={acceptedFileTypes.split(',')}
      onUpdate={() => {
      }}
      onImageUploaded={fileUploadProps.onImageUploaded}
      onAttachmentUploaded={fileUploadProps.onAttachmentUploaded}
      tags={gaId("file_upload_quick_win_idea_click")}
      disabled={disabled}
    >
      <FileUploadTrigger disabled={disabled}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" size="icon" variant="outline" disabled={disabled}>
              <Paperclip className="dark:text-white"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{buttonTitle}</TooltipContent>
        </Tooltip>
      </FileUploadTrigger>
    </FileUpload>
  );
  const renderedChildren = typeof children === "function" ? children({handlePaste, uploadTrigger}) : children;
  const renderedFooter = typeof footer === "function" ? footer({uploadTrigger}) : footer;

  return <div className={cn("space-y-2 relative", className)} {...dragHandlers}>
    <FileDropOverlay visible={dragActive} className="rounded-lg"/>
    {renderedChildren}
    {hasFiles && <FilePreviewList
        imageUrls={imageUrls}
        attachments={attachments}
        onDeleteImage={(index) => {
          setImageUrls(prev => prev.filter((_, i) => i !== index));
        }}
        onDeleteAttachment={(id) => {
          setAttachments(prev => prev.filter((a: AttachmentInfo) => a.id !== id));
        }}
    />}
    {hasLinks && <LinkPreviewList
        companyId={companyId}
        links={detectedLinks}
        onRemove={handleRemoveLink}
        onPreviewLoaded={handlePreviewLoaded}
    />}
    {renderedFooter}
    {!hideUploadTrigger && uploadTrigger}
  </div>
}
